/**
 * Capability matrix — the single client-side mirror of the server matrix in
 * migration 00131 (role_has_capability). The DATABASE is authoritative; this
 * drives UX gating only. Kept pure (no React/store deps) so it's unit-testable
 * and importable anywhere.
 *
 * @since 1.0.0
 */

export type Capability =
    | 'view'
    | 'create_draft'
    | 'post_transaction'
    | 'manage_members'
    | 'manage_org'
    | 'audit_adjust'
    /**
     * Issue the papers only an auditor may issue — the SA 210 engagement
     * letter, the SA 580 management representation letter, the audit report,
     * 3CA/3CB/3CD. Held by `auditor` ALONE, deliberately: an owner or admin
     * issuing the opinion on their own company is the exact thing the aud lane
     * exists to prevent.
     */
    | 'audit_docs';

export type OrgRole = 'owner' | 'admin' | 'consultant' | 'data_entry' | 'auditor' | 'viewer' | 'staff';

export const ALL_CAPABILITIES: Capability[] = [
    'view', 'create_draft', 'post_transaction', 'manage_members', 'manage_org',
    'audit_adjust', 'audit_docs',
];

// Lock-step with role_has_capability() — 00131_rbac.sql, then
// 00279_audit_adjustments.sql (audit_adjust), then aud-1
// (20260808162719_aud1_auditor_role_…), which split `auditor` out of `viewer`
// and added `audit_docs`.
export const CAPABILITY_MATRIX: Record<OrgRole, Capability[]> = {
    owner: ['view', 'create_draft', 'post_transaction', 'manage_members', 'manage_org', 'audit_adjust'],
    admin: ['view', 'create_draft', 'post_transaction', 'manage_members', 'manage_org', 'audit_adjust'],
    consultant: ['view', 'create_draft', 'post_transaction', 'audit_adjust'],
    data_entry: ['view', 'create_draft'],
    // The statutory / tax auditor: reads the books, passes audit adjustments,
    // issues their own documents — and does nothing else to the business.
    auditor: ['view', 'audit_adjust', 'audit_docs'],
    // Read-only, full stop. Until aud-1 this role also carried `audit_adjust`
    // (00279 granted it to the whole viewer tier), so a bank or an investor
    // handed "read-only" access could post adjusting entries into the books and
    // lock the financial year.
    viewer: ['view'],
    // Practice employee — draft-only like data_entry, but a distinct tier so
    // the firm surfaces recognise a staffer and route them to My Work. Never
    // posts, moves money, or manages members/org. Mirrors migration
    // 20260724074931 (role_has_capability 'staff').
    staff: ['view', 'create_draft'],
};

/** Pure capability check — mirrors the DB function of the same intent. */
export function roleHasCapability(role: string, cap: Capability): boolean {
    return (CAPABILITY_MATRIX[role as OrgRole] ?? []).includes(cap);
}

/** Plain, user-facing names for the stored role strings. */
export const ROLE_LABELS: Record<string, string> = {
    owner: 'Owner',
    admin: 'Admin',
    consultant: 'Accountant',
    data_entry: 'Employee',
    auditor: 'Auditor',
    viewer: 'Viewer',
    staff: 'Team member',
};

/** One-line descriptions for role pickers. */
export const ROLE_DESCRIPTIONS: Record<string, string> = {
    owner: 'Complete control, including billing and ownership',
    admin: 'Manage members and settings; full books access',
    consultant: 'Post, edit and void transactions; run reports',
    data_entry: 'Enter day-to-day data as drafts (an Accountant/Admin posts)',
    auditor: 'Your statutory or tax auditor — reads the books, passes audit adjustments, and issues their own report',
    viewer: 'Read-only access to everything; cannot make changes',
    staff: 'A firm employee — sees the work queue and their assigned clients only',
};

/** Roles assignable in the UI, in privilege order (owner handled separately). */
export const ASSIGNABLE_ROLES: OrgRole[] = ['admin', 'consultant', 'data_entry', 'auditor', 'viewer'];
